import { Channel, useChannelsQuery } from 'api/ChannelsAPI';
import { useCurrentProject, useProjectTimeZones } from 'api/ProjectsAPI';
import { fetchCampaignLabelsQuery } from 'api/queries';
import { MessageMedium } from 'constants/messageMediums';
import { selectIsBossOrSalesBossLevel } from 'core/userContext';
import { selectAllOrganizationsList } from 'modules/allOrganizations';
import { fetchAllOrganizationsRequest } from 'modules/allOrganizations/actions';
import { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sortObjectListByKey } from 'utils/arrayHelpers';
import { useGraphql } from 'utils/hooks/useGraphql';
import { ProjectLabel } from '../types';
import { ProjectSettingsQueryContext } from './ProjectSettingsQueryContext';

export const ProjectSettingsQueryContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const isBaus = useSelector(selectIsBossOrSalesBossLevel);
  const { data: project = {}, refetch: refetchProject } = useCurrentProject({
    refetchOnWindowFocus: false,
  });
  const {
    data: { allowedTimeZones: timeZones = [] } = {},
  } = useProjectTimeZones({ refetchOnWindowFocus: false });
  const organization = useSelector(selectAllOrganizationsList)?.[project?.organizationId];
  const { data: campaignLabelsData = {}, refetch: refetchLabels } =
    useGraphql(fetchCampaignLabelsQuery);
  const labels: ProjectLabel[] = campaignLabelsData?.campaignLabels || [];
  const { data: channels = [] } = useChannelsQuery({
    refetchOnWindowFocus: false,
  });
  const hasSmsChannel = channels?.some(
    (channel: Channel) => channel.messageMedium === MessageMedium.SMS,
  );

  useEffect(() => {
    if (isBaus) dispatch(fetchAllOrganizationsRequest() as any);
  }, [isBaus, dispatch]);

  return (
    <ProjectSettingsQueryContext.Provider
      value={{
        project,
        organization,
        timeZones,
        labels: sortObjectListByKey(labels, 'name'),
        refetchLabels,
        refetchProject,
        hasSmsChannel,
      }}
    >
      {children}
    </ProjectSettingsQueryContext.Provider>
  );
};
